/**
 * 單一任務條（一般／摘要／里程碑三種分支）與其互動熱區。
 *
 * 視覺語彙對齊 MS Project：
 * - 摘要 = 細括號（水平細線 + 兩端下折尖角），中間留空，不與子任務搶注意力
 * - 一般 = 約 1/3 列高的青色細條，進度為同色系深一階的中央窄帶
 * - 里程碑 = 小菱形，右側標任務名稱與日期
 */

import type { PointerEvent as ReactPointerEvent, ReactElement } from 'react';

import type { BarGeometry } from './geometry.js';
import {
  barBounds,
  barSpan,
  baselineGeometry,
  milestonePoints,
  progressBand,
  progressHitArea,
  summaryBarPath,
} from './geometry.js';
import type { TimeAxis } from './time-scale.js';
import type { DragKind, GanttTask, LinkAnchor } from './types.js';

/** 右緣調整工期的熱區寬度。 */
export const RESIZE_HANDLE_WIDTH = 7;
/** 建立依賴的圓形握把半徑。 */
export const LINK_HANDLE_RADIUS = 4;
/** 條與右側文字的間距。 */
const LABEL_GAP = 7;

/**
 * 色票（淺／深）。
 *
 * 深色模式沿用同一套語彙，只交換「紙」與「墨」：摘要括號與里程碑由黑轉為淺石墨色，
 * 青色任務條提亮一階以維持對比，其餘一律為中性灰階，全圖仍只有青／墨／要徑紅三個色相。
 */
const INK = 'fill-ink';
const INK_CRITICAL = 'fill-danger';
const BAR_FILL = 'fill-accent-bar';
const BAR_STROKE = 'stroke-accent';
const BAR_FILL_CRITICAL = 'fill-danger-bar';
const BAR_STROKE_CRITICAL = 'stroke-danger';
const PROGRESS_FILL = 'fill-accent-deep';
const PROGRESS_FILL_CRITICAL = 'fill-danger-deep';
const BASELINE_FILL = 'fill-ink-soft';
const LABEL_FILL = 'fill-fg';
const RESOURCE_FILL = 'fill-fg-subtle';
const SELECTION_STROKE = 'stroke-focus';
/**
 * 手動排程的錨點端線(MS Project 的作法)。
 *
 * 刻意不用「填色不同」來區分手動/自動:深色模式下填色差異很容易被底色吃掉,
 * 而端線是形狀差異,兩種主題下都讀得出來,也不會和要徑紅／進度色搶語意。
 */
const MANUAL_ANCHOR_STROKE = 'stroke-ink';
/** 端線在條的上下各外推的像素。 */
const MANUAL_ANCHOR_OVERHANG = 2;
/** 落後警示三角形的邊長。 */
const BEHIND_MARK_SIZE = 6;

/** `2026-07-23T08:00:00` → `7/23`（里程碑右側標註）。 */
function shortDate(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (match === null) return '';
  return `${Number(match[2])}/${Number(match[3])}`;
}

export interface TaskBarProps {
  readonly task: GanttTask;
  readonly bar: BarGeometry;
  readonly axis: TimeAxis;
  readonly selected: boolean;
  readonly showBaseline: boolean;
  readonly showCriticalPath: boolean;
  readonly onDragStart: (
    kind: DragKind,
    task: GanttTask,
    bar: BarGeometry,
    anchor: LinkAnchor | null,
    event: ReactPointerEvent,
  ) => void;
  readonly onSelect: (task: GanttTask, additive: boolean) => void;
}

export function TaskBar({
  task,
  bar,
  axis,
  selected,
  showBaseline,
  showCriticalPath,
  onDragStart,
  onSelect,
}: TaskBarProps): ReactElement {
  const critical = showCriticalPath && task.isCritical;
  const baseline = showBaseline ? baselineGeometry(task, bar, axis) : null;
  const bounds = barBounds(bar);
  const span = barSpan(bar);
  const progress = progressBand(bar);
  const progressHit = progressHitArea(bar, progress);

  // 摘要：名稱標在括號正上方；其餘：標在條的右側（里程碑加註日期）。
  const label =
    bar.kind === 'summary'
      ? { x: bar.x, y: bar.y - 3.5, anchor: 'start' as const, text: task.name }
      : {
          x: span.right + LABEL_GAP,
          y: bar.y + bar.height / 2 + 3.4,
          anchor: 'start' as const,
          text: bar.kind === 'milestone' ? `${task.name}（${shortDate(task.start)}）` : task.name,
        };

  return (
    <g
      data-testid="gantt-bar"
      data-task-id={task.id}
      data-bar-kind={bar.kind}
      data-critical={critical ? 'true' : 'false'}
      data-manually-scheduled={task.isManuallyScheduled ? 'true' : 'false'}
      onClick={(event) => {
        onSelect(task, event.metaKey || event.ctrlKey || event.shiftKey);
      }}
    >
      {baseline === null ? null : (
        <rect
          data-testid={`baseline-${task.id}`}
          x={baseline.x}
          y={baseline.y}
          width={baseline.width}
          height={baseline.height}
          className={BASELINE_FILL}
          pointerEvents="none"
        />
      )}
      {bar.kind === 'milestone' ? (
        <polygon
          data-testid={`milestone-${task.id}`}
          data-critical={critical ? 'true' : 'false'}
          points={milestonePoints(bar)}
          className={critical ? INK_CRITICAL : INK}
          onPointerDown={(event) => {
            onDragStart('move', task, bar, null, event);
          }}
        />
      ) : bar.kind === 'summary' ? (
        <path
          data-testid={`summary-${task.id}`}
          data-critical={critical ? 'true' : 'false'}
          d={summaryBarPath(bar)}
          className={critical ? INK_CRITICAL : INK}
          shapeRendering="crispEdges"
          onPointerDown={(event) => {
            onDragStart('move', task, bar, null, event);
          }}
        />
      ) : (
        <>
          <rect
            data-testid={`bar-body-${task.id}`}
            data-critical={critical ? 'true' : 'false'}
            x={bar.x}
            y={bar.y}
            width={bar.width}
            height={bar.height}
            rx={1}
            className={
              critical
                ? `${BAR_FILL_CRITICAL} ${BAR_STROKE_CRITICAL}`
                : `${BAR_FILL} ${BAR_STROKE}`
            }
            strokeWidth={0.75}
            style={{ cursor: 'grab' }}
            onPointerDown={(event) => {
              onDragStart('move', task, bar, null, event);
            }}
          />
          {bar.progressWidth > 0 ? (
            <rect
              data-testid={`bar-progress-${task.id}`}
              x={bar.x}
              y={progress.y}
              width={bar.progressWidth}
              height={progress.height}
              className={critical ? PROGRESS_FILL_CRITICAL : PROGRESS_FILL}
              pointerEvents="none"
            />
          ) : null}
          {/* 進度握把：進度細線右緣。命中區比細線上下各多留幾 px，但刻意不吃掉整條 —— 條的上下緣仍是「移動任務」。 */}
          <rect
            data-testid={`progress-handle-${task.id}`}
            x={bar.x + bar.progressWidth - 3}
            y={progressHit.y}
            width={6}
            height={progressHit.height}
            className="fill-transparent"
            style={{ cursor: 'col-resize' }}
            onPointerDown={(event) => {
              onDragStart('progress', task, bar, null, event);
            }}
          />
          {/* 工期握把：條右緣 */}
          <rect
            data-testid={`resize-handle-${task.id}`}
            x={bar.endX - RESIZE_HANDLE_WIDTH / 2}
            y={bar.y}
            width={RESIZE_HANDLE_WIDTH}
            height={bar.height}
            className="fill-transparent"
            style={{ cursor: 'ew-resize' }}
            onPointerDown={(event) => {
              onDragStart('resize', task, bar, null, event);
            }}
          />
        </>
      )}

      {/*
        落後預期進度的警示三角。與要徑紅分開（見 GanttTask.behindPercent），
        且畫在條的正上方而非覆蓋條身 —— 覆蓋上去會在左上角形成一塊拖不動的死區。
      */}
      {typeof task.behindPercent === 'number' && task.behindPercent > 0 ? (
        <polygon
          data-testid={`behind-mark-${task.id}`}
          points={[
            `${span.left},${bounds.top - 1}`,
            `${span.left + BEHIND_MARK_SIZE},${bounds.top - 1}`,
            `${span.left + BEHIND_MARK_SIZE / 2},${bounds.top - 1 - BEHIND_MARK_SIZE}`,
          ].join(' ')}
          className="fill-warn"
        >
          <title>{`落後 ${Math.round(task.behindPercent)}%`}</title>
        </polygon>
      ) : null}

      {/* 手動排程：兩端豎線的錨點樣式 */}
      {task.isManuallyScheduled ? (
        <g data-testid={`manual-anchor-${task.id}`} pointerEvents="none">
          <line
            x1={span.left - 0.5}
            y1={bounds.top - MANUAL_ANCHOR_OVERHANG}
            x2={span.left - 0.5}
            y2={bounds.bottom + MANUAL_ANCHOR_OVERHANG}
            className={MANUAL_ANCHOR_STROKE}
            strokeWidth={1.75}
            shapeRendering="crispEdges"
          />
          <line
            x1={span.right + 0.5}
            y1={bounds.top - MANUAL_ANCHOR_OVERHANG}
            x2={span.right + 0.5}
            y2={bounds.bottom + MANUAL_ANCHOR_OVERHANG}
            className={MANUAL_ANCHOR_STROKE}
            strokeWidth={1.75}
            shapeRendering="crispEdges"
          />
        </g>
      ) : null}

      {/* 依賴握把（左 = start 端，右 = finish 端） */}
      <circle
        data-testid={`link-start-${task.id}`}
        cx={span.left - LINK_HANDLE_RADIUS - 2}
        cy={bar.midY}
        r={LINK_HANDLE_RADIUS}
        className="fill-transparent hover:fill-fg-subtle/70"
        style={{ cursor: 'crosshair' }}
        onPointerDown={(event) => {
          onDragStart('link', task, bar, 'start', event);
        }}
      />
      <circle
        data-testid={`link-finish-${task.id}`}
        cx={span.right + LINK_HANDLE_RADIUS + 2}
        cy={bar.midY}
        r={LINK_HANDLE_RADIUS}
        className="fill-transparent hover:fill-fg-subtle/70"
        style={{ cursor: 'crosshair' }}
        onPointerDown={(event) => {
          onDragStart('link', task, bar, 'finish', event);
        }}
      />

      {label.text === '' && task.resourceNames === '' ? null : (
        <text
          x={label.x}
          y={label.y}
          textAnchor={label.anchor}
          className={`${LABEL_FILL} text-[10px]`}
          pointerEvents="none"
        >
          <tspan data-testid={`label-${task.id}`}>{label.text}</tspan>
          {task.resourceNames === '' ? null : (
            <tspan
              data-testid={`resources-${task.id}`}
              dx={label.text === '' ? 0 : 6}
              className={RESOURCE_FILL}
            >
              {task.resourceNames}
            </tspan>
          )}
        </text>
      )}

      {selected ? (
        <rect
          data-testid={`selection-${task.id}`}
          x={span.left - 2.5}
          y={bounds.top - 2.5}
          width={span.right - span.left + 5}
          height={bounds.bottom - bounds.top + 5}
          rx={2}
          className={`fill-none ${SELECTION_STROKE}`}
          strokeWidth={1.25}
          pointerEvents="none"
        />
      ) : null}
    </g>
  );
}
